'use client';
import { Link } from '@/navigation';
import { LoanTypeLabel } from '@/types/loan';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PaidIcon from '@mui/icons-material/Paid';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import Image, { StaticImageData } from 'next/image';
import { useState } from 'react';
import style from './style.module.css';
import LinkWithParameters from '../LinkWithParameters';

interface CardProps {
  id: string;
  imageCard: StaticImageData | string;
  title: string;
  labelButton: string;
  texts?: { label: string; value: string }[];
  variant?: 'primary' | 'secondary';
  cardSlug?: string;
  pageType: 'CreditCardPage' | 'LoanPage';
  countryCode: string;
}

const Card = ({
  imageCard,
  title,
  texts,
  labelButton,
  variant = 'primary',
  cardSlug = '',
  pageType,
  countryCode,
}: CardProps) => {
  const link = cardSlug
    ? pageType === 'CreditCardPage'
      ? '/cartao-de-credito/[slug]'
      : '/emprestimo/[slug]'
    : cardSlug;

  const [isImageError, setImageError] = useState(false);
  const t = useTranslations('Index');

  const handleImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.target;
    if (naturalWidth === 0 || (naturalWidth === 1 && naturalHeight === 1)) {
      setImageError(true);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const renderLabel = (value: string) => {
    // This label handling should be reviewed
    if (pageType === 'LoanPage' && LoanTypeLabel[value])
      return t(`loans.loanType.${value}`);
    if (value === 'filter.flexible') return t('filter.flexible');

    if (value === 'cards.noAnnualFee') return t('cards.noAnnualFee');

    if (value === 'cards.noInfo') return t('cards.noInfo');

    return value;
  };

  return (
    <div className={classNames(style.containerCard, style[variant])}>
      <div className={style.contentImage}>
        {!isImageError ? (
          <Image
            className={style.imageCard}
            alt="Card Image"
            src={imageCard}
            width={356}
            height={112}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        ) : pageType === 'CreditCardPage' ? (
          <CreditCardIcon className={style.iconErrorCreditCard} />
        ) : pageType === 'LoanPage' ? (
          <PaidIcon className={style.iconErrorLoan} />
        ) : null}
      </div>
      <div className={style.contentText}>
        <h3 className={style.cardTitle}>{title}</h3>
        <div className={style.textsContainer}>
          {texts?.map(({ label, value }) => (
            <div key={label} className={style.textWrapper}>
              <p className={style.text}>
                <b>{t(label)}: </b>
                <span>{renderLabel(value)}</span>
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className={style.contentButton}>
        <LinkWithParameters
          className={style.cardButton}
          href={link as any}
          slug={cardSlug}
          country={countryCode}
          vertical={pageType == 'LoanPage' ? 'emp' : 'cc'}>
          {t(labelButton)}
        </LinkWithParameters>
        {variant === 'secondary' && (
          <div className={style.contentSecureLink}>
            <LockOutlinedIcon fontSize="small" />
            <p>{t('cards.secureLink')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Card;
